import React, { useEffect, useState } from 'react';
import { IonModal, IonText, IonIcon } from '@ionic/react';
import { checkmarkOutline } from 'ionicons/icons';
import { useDispatch } from 'react-redux';
import { SlotCompareReduxCommandCreator } from 'appRedux/actions/slotCompareCommandCreator';
import { useAppSelector } from 'appRedux/createStore';
import { playerTypeEnum } from 'common/commonEnum';
import { InsightFilterEnum } from 'appRedux/models/favoritesModels';
import { shouldShowPop } from 'lib/showPop';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface IInsightOption {
  id: string;
  label: string;
  playerTypeAccess: playerTypeEnum[];
}

export interface ISection {
  section: string;
  icon: string;
  options: IInsightOption[];
}

const insightLabelMap: Record<InsightFilterEnum, string> = {
  [InsightFilterEnum.PayoutToPlayer]: 'POP',
  [InsightFilterEnum.ReturnToPlayer]: 'RTP',
  [InsightFilterEnum.Jackpot]: 'Jackpots',
  [InsightFilterEnum.MoneyWon]: '$ Won',
  [InsightFilterEnum.AverageWonPerSpin]: '$ Wins/Spin',
  [InsightFilterEnum.MoneyPlayed]: '$ Played',
  [InsightFilterEnum.Spins]: 'Spins',
  [InsightFilterEnum.WinRate]: 'Win Rate',
  [InsightFilterEnum.Volatility]: 'Vol',
  [InsightFilterEnum.NonJackpotWonPerSpin]: 'W/S-J',
  [InsightFilterEnum.JackpotWonPerSpin]: 'J/S',
};

export const useSortByOptions = (): ISection[] => {
  const { selectedInsight } = useAppSelector((state) => state.app.persistedState.compareSlotsState);
  // POP is only offered as a sort when the current casino has showPOP = 1.
  const showPop = useAppSelector((state) => shouldShowPop(state.app.persistedState.casinoSchema));

  const insightsOptions: IInsightOption[] = [
    {
      id: InsightFilterEnum.PayoutToPlayer,
      label: 'POP',
      playerTypeAccess: [playerTypeEnum.slotCheckPro],
    },
    {
      id: InsightFilterEnum.ReturnToPlayer,
      label: 'RTP',
      playerTypeAccess: [playerTypeEnum.slotCheckPro],
    },
    {
      id: InsightFilterEnum.Jackpot,
      label: 'Jackpots',
      playerTypeAccess: [playerTypeEnum.slotCheckPro],
    },
  ];

  if (selectedInsight && !insightsOptions.some((opt) => opt.id === selectedInsight)) {
    if (Object.values(InsightFilterEnum).includes(selectedInsight as InsightFilterEnum)) {
      insightsOptions.push({
        id: selectedInsight as InsightFilterEnum,
        label: insightLabelMap[selectedInsight as InsightFilterEnum] ?? selectedInsight,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      });
    }
  }

  const visibleInsightOptions = showPop
    ? insightsOptions
    : insightsOptions.filter((opt) => opt.id !== InsightFilterEnum.PayoutToPlayer);

  return [
    {
      section: 'Insights',
      icon: 'assets/icon/insights-popup-section.svg',
      options: visibleInsightOptions,
    },
    {
      section: 'Other',
      icon: 'assets/icon/range-popup-section.svg',
      options: [
        {
          id: 'game_name',
          label: 'Game Name',
          playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
        },
        {
          id: 'slot_number',
          label: 'Slot Number',
          playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
        },
      ],
    },
  ];
};

export const SortByPopup: React.FC<Props> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const slotCompareCommands = SlotCompareReduxCommandCreator(dispatch);
  const { compareSortBy } = useAppSelector((state) => state.app.persistedState.compareSlotsState);
  const sortByOptions = useSortByOptions();

  const [localSortBy, setLocalSortBy] = useState<string>(compareSortBy);

  useEffect(() => {
    if (isOpen) {
      setLocalSortBy(compareSortBy); 
    }
  }, [isOpen, compareSortBy]);

  const handleSave = () => {
    if (localSortBy !== compareSortBy) {
      slotCompareCommands.setSortBy({ compareSortBy: localSortBy });
    }
    onClose();
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      className="settings-modal"
      backdropDismiss={true}
    >
      <div className="settings-popup">
        <IonText className="settings-title">Sort By</IonText>

        {sortByOptions.map((section) => (
          <div className="settings-section" key={section.section}>
            <div className="settings-title-row">
              <IonIcon icon={section.icon} className="settings-icon" />
              <IonText className="section-title">{section.section}</IonText>
            </div>

            <div className="button-group">
              {section.options.map((option) => (
                <button
                  key={option.id}
                  className={localSortBy === option.id ? 'filled' : 'outline'}
                  onClick={() => setLocalSortBy(option.id)}
                >
                  {localSortBy === option.id && <IonIcon icon={checkmarkOutline} />}
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div className="settings-footer">
          <button className="outline" onClick={onClose}>
            Close
          </button>
          <button className="filled" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </IonModal>
  );
};
